import React from 'react'
import Autocomplete from '@material-ui/lab/Autocomplete'
import type { AutocompleteProps } from '@material-ui/lab/Autocomplete'
import Checkbox from '@material-ui/core/Checkbox'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import clsx from 'clsx'
import Typography from '@material-ui/core/Typography'

import { useAutocompleteWrapperStyles } from './autocomplete-wrapper.styles'
import { useTranslation } from '@plandi/common'

type AutocompleteWrapperProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> = AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> & {
  getOptionTag?: (tag: T) => string
}

export const AutocompleteWrapper = <
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>({
  getOptionTag,
  ...props
}: AutocompleteWrapperProps<T, Multiple, DisableClearable, FreeSolo>) => {
  const classes = useAutocompleteWrapperStyles()
  const { data: translate } = useTranslation()
  const multiplyProps = props.multiple
    ? {
        disableCloseOnSelect: true,
        openOnFocus: true,
        renderTags: (tags: T[]) => (
          <Typography className={classes.chip} noWrap>
            {tags
              .map((tag) =>
                getOptionTag ? getOptionTag(tag) : props.getOptionLabel?.(tag)
              )
              .join(', ')}
          </Typography>
        )
      }
    : {}

  return (
    <Autocomplete
      fullWidth
      noOptionsText={translate?.['no-data']}
      renderOption={(option, { selected }) => (
        <>
          {props.multiple ? (
            <>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                className={classes.checkbox}
                checked={selected}
              />
              {props?.getOptionLabel?.(option)}
            </>
          ) : (
            <>{props?.getOptionLabel?.(option)}</>
          )}
        </>
      )}
      {...props}
      classes={{
        ...props.classes,
        inputRoot: clsx(classes.inputRoot, props.classes?.inputRoot)
      }}
      {...multiplyProps}
    />
  )
}
